import { HOLD_PREFIX, PIPELINE_STAGES, type ContractDetail, type ProcessingStatus } from '@clm/shared-types';

/**
 * The pure half of the pipeline stage tracker — CLM-70 (D-70-5). Kept out of
 * `PipelineStageTracker.tsx` so that file exports only a component and fast
 * refresh stays whole (the `panel-state.ts` split).
 *
 * `stepStates` derives the five `data-state`s from the record alone;
 * `trackerNote` the one-line explanation under the rail. Both read only what
 * `retrieve-contract` already returns — no second request, no guess.
 */

export type StepState = 'done' | 'current' | 'blocked' | 'skipped' | 'todo';

function upTo(doneCount: number, current: number | null): StepState[] {
  return PIPELINE_STAGES.map((_, index) => {
    if (index < doneCount) return 'done';
    if (current !== null && index === current) return 'current';
    return 'todo';
  });
}

/**
 * The five states for one contract.
 *
 * - `PendingExtraction` — the first step is current, nothing reached.
 * - `Converted` / `Pseudonymised` / `Extracted` — done up to there, the next
 *   step current.
 * - `PendingVerification` — Extracted done, the last step current (FR-07 pending).
 * - `Verified` — all five done: nothing is outstanding. The trust advance writes
 *   it when the last outstanding field is settled; CLM-108 made that true of an
 *   auto-approved contract too, which `trackerNote` says.
 * - `manual_entry_pending` — Converted done, Pseudonymised and Extracted
 *   **skipped** (§4C: withheld, not failed).
 * - `ExtractionFailed` — the blocked step comes from `ai_processing`: a §4A.5
 *   hold (the runner's `HOLD_PREFIX` in `failure_reason`) blocks
 *   Pseudonymised; a refusal or any other failure blocks Extracted.
 */
export function stepStates(contract: Pick<ContractDetail, 'processing_status' | 'ai_processing'>): readonly StepState[] {
  const status: ProcessingStatus = contract.processing_status;
  switch (status) {
    case 'PendingExtraction':
      return upTo(0, 0);
    case 'Converted':
      return upTo(2, 2);
    case 'Pseudonymised':
      return upTo(3, 3);
    case 'Extracted':
    case 'PendingVerification':
      return upTo(4, 4);
    case 'Verified':
      return upTo(5, null);
    case 'manual_entry_pending':
      return ['done', 'done', 'skipped', 'skipped', 'todo'];
    case 'ExtractionFailed': {
      const ai = contract.ai_processing;
      const heldAtPseudonymise = ai.ai_processing_state === 'failed' && ai.failure_reason.startsWith(HOLD_PREFIX);
      return heldAtPseudonymise
        ? ['done', 'done', 'blocked', 'todo', 'todo']
        : ['done', 'done', 'done', 'blocked', 'todo'];
    }
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

/**
 * The one-line note under the rail; `null` when the shape needs no explanation. Reasons are the record's text (CR-01).
 *
 * CLM-108 (R-108-14, D-2.1-4) — `data_trust` is in the `Pick` because a settled
 * pipeline no longer implies that a person read every field: for `Verified`
 * beside `data_trust = 'AutoApproved'` the note says so, rather than leaving a
 * rail of five done steps to read as "verified".
 */
export function trackerNote(contract: Pick<ContractDetail, 'processing_status' | 'ai_processing' | 'data_trust'>): string | null {
  const ai = contract.ai_processing;
  switch (contract.processing_status) {
    case 'Verified':
      return contract.data_trust === 'AutoApproved'
        ? 'Settled — some fields were auto-approved by policy and not reviewed by a person (CR-06).'
        : null;
    case 'PendingVerification':
    case 'Extracted':
      return 'Extracted — awaiting human verification (FR-07).';
    case 'manual_entry_pending':
      return 'Withheld — processing restricted (§4C). Facts are entered by a person; this is a decision, not a failure.';
    case 'ExtractionFailed':
      if (ai.ai_processing_state === 'refused') {
        return `Refused by the model (${ai.refusal_category ?? 'no category'}) — routed to human review, never to a fallback model.`;
      }
      if (ai.ai_processing_state === 'failed') {
        return ai.failure_reason.startsWith(HOLD_PREFIX)
          ? `Held before the send — ${ai.failure_reason}`
          : `Extraction failed — ${ai.failure_reason}`;
      }
      return 'Extraction failed — the audit trail carries the reason.';
    default:
      return null;
  }
}
